import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../../components/styled/Button';
import apiService from '../../../../services/apiService';
import { getChatHistory, saveChatHistory } from '../../services/hestonService';

const styles = {
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '600px',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 10px 25px rgba(0, 119, 181, 0.05)',
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    background: 'linear-gradient(180deg, #fff 0%, #f9fafb 100%)',
  },
  messageRow: {
    display: 'flex',
    width: '100%',
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: '#0077B5',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: '18px 18px 2px 18px',
    maxWidth: '85%',
    fontSize: '14px',
    lineHeight: '1.5',
    boxShadow: '0 2px 8px rgba(0, 119, 181, 0.2)',
  },
  assistantMessage: {
    alignSelf: 'flex-start',
    backgroundColor: '#fff',
    color: '#1f2937',
    border: '1px solid #e5e7eb',
    padding: '12px 16px',
    borderRadius: '18px 18px 18px 2px',
    maxWidth: '85%',
    fontSize: '14px',
    lineHeight: '1.5',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    whiteSpace: 'pre-wrap',
  },
  inputArea: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e7eb',
    gap: '12px',
  },
  inputField: {
    flex: 1,
    padding: '12px 18px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '28px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  },
  typingIndicator: {
    color: '#6b7280',
    fontSize: '12px',
    fontStyle: 'italic',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  }
};

const tableStyles = {
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
    fontSize: '12px',
    margin: '8px 0',
    lineHeight: '1.4',
  },
  th: {
    backgroundColor: '#f3f4f6',
    fontWeight: 600,
    padding: '6px 10px',
    borderBottom: '2px solid #d1d5db',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '5px 10px',
    borderBottom: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
  },
  trEven: {
    backgroundColor: '#f9fafb',
  },
};

const parseMarkdownTable = (lines) => {
  // Need at least header + separator + 1 data row
  if (lines.length < 3) return null;
  const sepLine = lines[1].trim();
  if (!/^\|[\s:-]+(\|[\s:-]+)+\|?$/.test(sepLine)) return null;

  const parseRow = (line) =>
    line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());

  const headers = parseRow(lines[0]);
  const rows = [];
  for (let i = 2; i < lines.length; i++) {
    if (!lines[i].includes('|')) break;
    rows.push(parseRow(lines[i]));
  }
  if (rows.length === 0) return null;
  return { headers, rows, lineCount: 2 + rows.length };
};

const InlineBold = ({ text }) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
};

const FormattedText = ({ text }) => {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    // Try to parse a markdown table starting at this line
    if (lines[i].includes('|') && i + 2 < lines.length) {
      const table = parseMarkdownTable(lines.slice(i));
      if (table) {
        elements.push(
          <div key={`tbl-${i}`} style={{ overflowX: 'auto' }}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  {table.headers.map((h, hi) => (
                    <th key={hi} style={tableStyles.th}><InlineBold text={h} /></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, ri) => (
                  <tr key={ri} style={ri % 2 === 0 ? {} : tableStyles.trEven}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={tableStyles.td}><InlineBold text={cell} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        i += table.lineCount;
        continue;
      }
    }

    // Detect inline pipe-separated data (e.g., "FL: 10.8% | CA: 8.54% | AZ: 7.55%")
    // Must have 3+ segments separated by | to avoid false positives
    const trimmed = lines[i].replace(/^[-•*]\s*/, '').trim();
    if (trimmed.includes('|') && !trimmed.startsWith('|')) {
      const segments = trimmed.split('|').map(s => s.trim()).filter(s => s.length > 0);
      if (segments.length >= 3) {
        // Check if segments look like key:value or key=value pairs
        const looksLikeData = segments.every(s => /[:=]/.test(s) || /\d/.test(s));
        if (looksLikeData) {
          elements.push(
            <div key={`inline-tbl-${i}`} style={{ overflowX: 'auto' }}>
              <table style={tableStyles.table}>
                <tbody>
                  <tr>
                    {segments.map((cell, ci) => (
                      <td key={ci} style={{ ...tableStyles.td, borderBottom: '2px solid #d1d5db' }}>
                        <InlineBold text={cell} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          );
          i++;
          continue;
        }
      }
    }

    // Horizontal rule (--- or ___ or ***)
    if (/^\s*[-_*]{3,}\s*$/.test(lines[i])) {
      elements.push(
        <hr key={`hr-${i}`} style={{ border: 'none', borderTop: '1px solid #d1d5db', margin: '10px 0' }} />
      );
      i++;
      continue;
    }

    // Markdown headers (# ## ### #### etc.)
    const headerMatch = lines[i].match(/^(#{1,4})\s+(.+)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const headerText = headerMatch[2];
      const headerStyle = {
        fontSize: level === 1 ? '18px' : level === 2 ? '15px' : level === 3 ? '13px' : '12px',
        fontWeight: 600,
        margin: level <= 2 ? '12px 0 6px' : '8px 0 4px',
        color: '#1f2937',
        lineHeight: '1.3',
      };
      elements.push(
        <div key={`h-${i}`} style={headerStyle}><InlineBold text={headerText} /></div>
      );
      i++;
      continue;
    }

    // Regular line — render with bold support
    elements.push(
      <React.Fragment key={`ln-${i}`}>
        {i > 0 && '\n'}
        <InlineBold text={lines[i]} />
      </React.Fragment>
    );
    i++;
  }

  return <>{elements}</>;
};

const ENCYCLOPEDIA = `
#0: Portfolio Address Explorer - Search, filter, and manage your address portfolio. Supports deleting and re-scraping.
#1: Price War Early Warning - Detects rapid competitive price drops that signal an emerging market conflict.
#2: Pricing Health Score - A 0-100 metric aggregating volatility, completeness, and consistency.
#3: Price Stability Index - Measures how often and how drastically prices change over time.
#4: Price Surprise Index - Identifies price changes that deviate significantly from historical moving averages.
#5: Anomaly Accumulation Monitor - Tracks the total volume of suspicious pricing events over time.
#6: Key Performance Indicators - 6-card grid displaying total addresses, data points, days tracking, avg price change, and trend indicators.
#7: Key Metrics Dashboard - Field-level KPI cards showing per-service pricing metrics (current average, % change, sparkline, range).
#8: Performance Benchmarks - Bullet chart comparing actual values against targets with qualitative ranges (poor/satisfactory/good).
#9: Portfolio Health Matrix - Dot-matrix heartbeat showing RAG (Red/Amber/Green) status for every address.
#10: Auto-generated Insights - Auto-generated bullet-point insights about pricing patterns and anomalies.
#11: Top Price Movers - Three-column ranking of addresses with biggest price increases, decreases, and most volatile prices.
#12: CERTUS Price Index - Fisher Ideal Index representing the aggregate inflation of your entire portfolio.
#13: Price Trend & Forecast - Line chart with historical price band, linear regression trend line, and 90-day forecast cone with R-squared.
#14: Price Change Calendar - GitHub-style calendar heatmap showing days with color intensity based on % of addresses that changed prices.
#15: State Summary with Sparklines - Sortable table of states with address count, price sparkline, average price, % change, range, and volatility.
#16: Data Quality Overview - RAG status table of all addresses with quality indicators, change %, and last scrape date; filterable.
#17: Service Price Indices - Multi-line time series comparing price trends across 4 service fields (Pest365, Pest365+, One-Time, ISV).
#18: Price Tier Distribution - Visualizes how addresses are clustered into Budget, Standard, and Premium tiers.
#19: Price Range Distribution - Histogram with 15 bins showing distribution of prices across the portfolio with quartile/median/mean stats.
#20: Pricing Portfolio Treemap - Rectangular treemap where tile size represents address count and color represents average price per tier.
#21: Service Tier Funnel - Funnel chart showing addresses flowing through price tiers (high to low) with conversion ratios.
#22: Price Stickiness Monitor - Circular gauge (0-100%) showing % of addresses with stable prices, plus trend badge and field-level metrics.
#23: Cumulative Price Changes - Waterfall chart showing starting price, increases (green), decreases (red), and final cumulative total.
#24: Price Movement by State - Slope chart with lines connecting each state's first and last price, stroke width scaled by address count.
#25: Price Architecture Analysis - Timeline grid showing service tier (rows) vs time (columns) with color indicating price levels.
#26: State Price Breakdown - Grouped bar chart comparing average prices per field (Pest365, Pest365+, One-Time, ISV) across states.
#27: Market Composition - Marimekko chart showing proportions of service fields and states with two-way decomposition.
#28: Premium Price Gap Analysis - Gap chart showing price differences between service tiers (premium vs standard) with colored bars.
#29: Price Heatmap Grid - Scrollable grid with states (rows) and service fields (columns) using sequential color scale for price intensity.
#30: State Price Rankings - Lollipop chart with states on y-axis sorted by ascending price with dots at the ends.
#31: State Rankings (Radial) - Radial bar chart with states arranged in a circle, bars radiating outward by price level.
#32: Price vs National Benchmark - Target chart comparing each state's price against national average with bullets and ranges.
#33: Geographic Map & State Comparison - High-level choropleth map for state-to-state pricing performance comparison.
#34: State Price Pyramid - Symmetric bar chart with states in center showing comparative metrics on left and right sides.
#35: State Pricing Profiles - Grid of radar charts per state showing 5-axis profile (Price, Volatility, Change Rate, Spread, Coverage).
#36: State Ranking Timeline - Bump chart showing how each state's rank changes over time with colored lines per state.
#37: State Pricing Radar - Multi-state radar chart with 5 axes (Pest365, Pest365+, One-Time, ISV, MTC) for price profile comparison.
#38: Geographic Cluster Map - Map visualization showing k-means pricing similarity clusters with states grouped by color.
#39: Competitive Position - Scatter plot positioning states by price level (x) vs price momentum (y) in competitive quadrants.
#40: State Competitive Landscape - 2D scatter of states colored by competitive tier showing price vs momentum with quadrant analysis.
#41: State Cluster Dendrogram - Hierarchical clustering tree (dendrogram) showing how states group by pricing similarity.
#42: Elasticity Heatmap - Cross-field heatmap showing price elasticity/correlation between service fields with color intensity.
#43: MTC Service Boundary - Geographic map showing mosquito/tick control service boundaries and availability zones by region.
#44: MTC Availability Profile - Small multiples grid (one chart per state) showing MTC pricing availability across date range.
#45: Historical Pricing Tracks - Row-based sparkline grid showing the price timeline for every single address.
#46: Weekly Price Trends - Multi-line chart showing average prices by week for each service field with confidence bands.
#47: Price Volatility Analysis - Statistical matrix measuring the standard deviation of prices by state/region.
#48: Rolling Price Momentum - Dual-axis chart showing RSI and MACD momentum indicators for price trend analysis.
#49: Seasonality Decomposition - Breaks prices into Trend, Seasonal, and Residual (Noise) components.
#50: Trend Path Visualization - Stream graph showing how different service tiers' price share evolves over time.
#51: Horizon Chart - Compressed time-series using colored bands to show magnitude and direction of price changes.
#52: Moving Average Envelopes - Multi-line chart showing price trends with overlaid moving average bands per service tier.
#53: Price Z-Score Timeline - Normalizes prices to standardized scores with threshold toggling to highlight statistical deviations.
#54: Box Plot (Over Time) - Price distributions across time periods with quartiles, median, and whiskers for temporal comparison.
#55: Price Change Gantt - Timeline bars representing price change events (spikes, drops, outliers, missing data) with severity coloring.
#56: Candlestick Chart - Financial-grade OHLC (Open/High/Low/Close) view of pricing sessions.
#57: Price Histogram Density - Multi-panel grid of histograms showing frequency distributions for each service tier.
#58: Ridgeline Plot - Overlapping kernel density estimates of price distributions across multiple dates.
#59: Beeswarm Plot - Scatter plot of individual price points grouped by state with circular packing to avoid overlap.
#61: Percentile Bands - Shaded area chart showing evolution of price percentile boundaries (10th-90th) over time.
#62: Price Decile Analysis - Dual-chart view: decile-to-decile transitions plus distribution of addresses per decile band.
#63: Herfindahl Index - Concentration metric tracking market dominance across service fields with competitive/monopolistic thresholds.
#64: Lorenz Curve - Cumulative distribution comparing price inequality across service tiers against diagonal equality line.
#65: Q-Q Plot - Quantile-Quantile comparison of price distributions against normal distribution to assess normality.
#66: Violin Plot - Mirrored kernel density distributions showing shape and spread of prices for each service category.
#67: Price Dispersion Map - Scatter chart mapping individual prices across time with dispersion metrics per period.
#69: Rolling Volatility - Time-series of standard deviation calculated over rolling windows with separate lines per service tier.
#70: Value at Risk (VaR) - Downside risk percentiles (95/99%) and tail statistics showing maximum expected price losses.
#74: Price Anomaly Timeline - Scatter plot with color-coded severity levels highlighting detected spikes, drops, and outlier events.
#76: Price Bandwidth Channels - Moving average centerline with dynamically calculated upper/lower bands tracking price variation envelope.
#77: Price Acceleration Analysis - Three-axis chart combining price level, velocity (1st derivative), and acceleration (2nd derivative).
#78: Variance Decomposition - Breakdown of total price variance into contributions by service tier and interaction effects.
#79: Volatility Heatmap - Color-grid matrix showing volatility levels by service field and time period.
#80: Volatility Rose Plot - Polar area chart displaying volatility magnitude for each service category as radial segments.
#81: Price Field Correlations - Measures how different service tiers (e.g. Pest365 vs One-Time) move together.
#82: Cross-Correlation Matrix - Time-lagged correlation heatmap showing how price movements in one field predict changes in others.
#83: Price Field Scatter Matrix - Pairwise scatter plots of all service price fields with trend lines and density coloring.
#84: State Correlation Network - Node-link graph showing correlation strength between state price movements as weighted connections.
#85: Price Parallel Coordinates - Multi-axis plot with brushable dimensions connecting price values across service tiers and time.
#86: Similarity Chord Diagram - Arc diagram showing similarity flows between address clusters with chord thickness proportional to similarity.
#87: Price Tier Migration Sankey - Flow diagram tracking address movement between price quartiles/tiers across consecutive periods.
#88: Pricing Hierarchy Sunburst - Drill-down explorer from National -> State -> City -> Address level.
#89: Service Coverage Treemap - Hierarchical rectangle layout sized by address count, colored by coverage/availability status.
#90: Quartile Migration Flow - Flow network depicting transitions of addresses from Q1-Q4 price quartiles with path tracking.
#91: Price Convergence Monitor - Trend indicator showing whether price distributions are converging, diverging, or stable.
#92: Address Similarity Matrix - Clustered heatmap comparing address price vectors with dendrograms for hierarchical grouping.
#93: Price Change Timeline - Chronological event list annotated with icons/colors indicating rate hikes, promos, and service changes.
#94: Structural Changepoint Map - Statistical detection of abrupt level shifts in price time-series with marked breakpoint locations.
#96: Address Outlier Map - Scatter plot identifying addresses as outliers based on statistical distance from price distribution centroid.
#97: Price Ratio Stability Chart - Multi-line ratios (Plus/Base, OneTime/Base, Initial/Base) with coefficient-of-variation stability verdicts.
#98: Price Bucket Migration Matrix - Heatmap table showing count of addresses moving between price bracket ranges period-over-period.
#99: Address Anomaly Scorecard - Grid of address cards with traffic-light scores (normal/watch/alert) for anomaly severity.
#100: Rank Stability Monitor - Line chart tracking rank position changes for top addresses over time with stability classification.
#101: Deviation from Benchmark - Horizontal bar chart comparing address prices against national, state, and historical mean benchmarks.
#102: Price Persistence (Memory) - Kaplan-Meier survival curve showing how long price levels hold before changing by service field.
#103: Price Path Divergence - Dual-panel: normalized price paths (spaghetti plot) and divergence index identifying most-divergent addresses.
#105: CUSUM Control Chart - Cumulative sum control chart with C+/C- statistics and UCL/LCL bounds for price drift detection.
#106: Shewhart Control Chart - Two-panel X-bar and range control chart with zone A/B/C sigma band shading for process monitoring.
#107: EWMA Control Chart - Exponentially-weighted moving average chart with field selection, target mean, and out-of-control highlighting.
#108: Price Entropy Analysis - Shannon entropy calculation with per-field breakdown showing disorder/randomness in price distribution.
#109: Benford's Law Diagnostic - Detects artificial pricing by checking if the first-digit distribution follows Benford's Law.
#110: Price Complexity Index - Complexity metric across service fields displayed as gauges measuring price structure intricacy.
#111: Markov Transition Matrix - Heatmap showing probability of price moving between states with interpretation guide and significance coloring.
#112: Bayesian Price Update - Three-curve visualization of prior distribution, likelihood function, and posterior distribution.
#113: Granger Causality Matrix - Correlation heatmap testing whether past prices in one field predict future prices in another.
#114: Lead-Lag Indicators - Lead-lag analysis across service field pairs with correlation measures and causal flow indicators.
#115: Runs Test Analysis - Timeline and histogram of price reversals detecting randomness vs systematic trends.
#116: Regime Transition Diagram - Alluvial/Sankey visualization showing price regime changes (stable/periodic/chaotic) across time.
#117: Spectral Density (FFT) - Frequency-domain analysis to find hidden cycles in your pricing history.
#118: Wavelet Scalogram - Time-frequency heatmap decomposing price oscillations across multiple frequency scales.
#119: Spectral Coherence Plot - Frequency-domain heatmap showing synchronized oscillations between service field pairs (0-1 coherence).
#120: State Synchronization Index - Line and bar charts tracking state price synchronization (0-1) with zone shading and trend arrows.
#121: Phase Portrait (State-Space) - 2D scatter of current price vs velocity/acceleration showing attractor basin and chaotic behavior.
#122: Lyapunov Exponent (Chaos) - Divergence analysis of nearby price trajectories classifying stability (stable/edge-of-chaos/chaotic).
#123: Impulse Response Function - Mini-charts showing cumulative response when one service field receives a price shock.
#124: Recurrence Plot (Dynamics) - Distance matrix heatmap visualizing recurring price patterns in phase space over time intervals.
#125: Regression Discontinuity - Scatter with fitted curves before/after a cutoff date showing structural break in price relationships.
#126: Quantile Regression Fan - Time series with 10th/25th/50th/75th/90th percentile bands with spread status badge.
#127: Residuals Plot (Diagnostics) - Three-panel residual diagnostics: vs fitted (scatter), histogram, and Q-Q plot for normality assessment.
#128: Price Velocity Profile - Bars for discrete daily velocity + acceleration overlay line with upward/downward drift detection.
#129: ARIMA Forecast Cone - Advanced autoregressive forecasting with 95% confidence bands.
#130: Monte Carlo Fan Chart - Simulates 1,000+ stochastic What-If price paths for risk assessment.
#131: Ensemble Forecast Cloud - Multiple forecast lines from 4 methods (linear, EMA, mean reversion, random walk) + ensemble average.
#132: Price Half-Life Decay - Half-life duration (periods for price to decay 50%) with field selection and info cards.
#133: Price Contagion Map - Geographic heatmap of state-to-state price spillover effects with regional correlation.
#134: Information Ratio Profile - Excess return vs benchmark risk with ratio summary line and benchmark indicator.
#135: Counterfactual Analysis - Actual vs counterfactual (what-if) price scenario paths overlaid with divergence highlighting.
#136: Event Study Analysis - Cumulative abnormal return around event date with significance badge and per-field contribution.
#137: Signal vs Noise Decomposition - Two-panel decomposition: extracted signal + residual noise with filter visualization.
#138: Performance Attribution - Waterfall/bridge chart decomposing total return into factor contributions (drag/lift per component).
#139: Price Trend Ribbons - Multi-colored field ribbons flowing over time showing parallel trend paths with field selector.
#140: Bundle Yield Curve - Service bundle composition chart with yield on left axis and margin efficiency % on right axis.
#141: Urban vs Rural Divergence - Geographic comparison showing price differences between urban and rural service areas.
#142: PCA Biplot (Variance) - Identifies the primary hidden factors driving 80%+ of your pricing variance.
#143: t-SNE Cluster Projection - 2D scatter of addresses reduced via t-SNE with cluster coloring and count legend.
#144: Factor Loadings Analysis - Heatmap of 3 latent factors' loadings on each service field with side panel stats.
#145: Relative Strength Index (RSI) - RSI momentum line (0-100) with overbought/oversold zones and current value/signal badge.
#148: Dumbbell (Max-Min Range) - Horizontal dumbbell chart showing min-max price spreads per state/field, sortable by range width.
#152: Detected Anomalies List - Anomaly scorecard listing detected pricing irregularities with severity classifications.
#155: Psychological Pricing Density - Detects the prevalence of charm pricing (e.g., .99 or .95 endings).
`;

const TAB_MAP = `
DASHBOARD TAB NAVIGATION (tell users which tab to click):
- Price Intelligence: #0, #17-#32
- Executive Summary: #1-#16
- Geographic & State: #33-#44, #141
- Time Series: #45-#56
- Distributions: #57-#67
- Volatility & Risk: #69, #70, #74, #76-#80
- Correlations & Networks: #81-#92
- Change Detection: #93-#103
- Statistical Models: #105-#116
- Spectral & Dynamics: #117-#128
- Forecasting: #129-#140
- Data Quality: #142-#145, #148, #152
- Extra: #155
`;

const SERVICE_FIELDS = `
SERVICE FIELD GLOSSARY (the 5 pricing fields tracked per address):
- Pest365: Annual pest control subscription (base tier, most common)
- Pest365+: Premium annual subscription with enhanced coverage
- One-Time: Single-visit pest treatment (non-recurring, higher per-visit price)
- ISV (Initial Service Visit): First-visit setup fee for new customers
- MTC (Mosquito & Tick Control): Seasonal add-on service — NOT available in all states/regions
`;

const SYSTEM_PROMPT = `STRICT OPERATING INSTRUCTIONS:
You are Heston, the AI Product Manager and Data Analyst for a pest control pricing intelligence dashboard with 142 components.

ROLES:
1. PRODUCT EXPERT: Guide users to the correct dashboard component using the Encyclopedia and Tab Map below.
2. DATA ANALYST: Answer data questions using ONLY the provided snapshot — never invent numbers.

${SERVICE_FIELDS}

FULL COMPONENT ENCYCLOPEDIA (search this THOROUGHLY before answering):
${ENCYCLOPEDIA}

${TAB_MAP}

CRITICAL RULES:
1. PRODUCT QUERIES — SEARCH THE FULL ENCYCLOPEDIA: When a user asks about a feature, scan ALL entries above. Many components have non-obvious names (e.g., "candlestick" = #56, "Benford" = #109, "volatility" = #47 or #69, "RSI" = #145, "Lorenz" = #64, "Sankey" = #87, "Markov" = #111, "Herfindahl" = #63, "t-SNE" = #143, "PCA" = #142). If a component EXISTS, you MUST name it with its # and description. If it truly does NOT exist, propose it as #156+.
2. MATCH MULTIPLE COMPONENTS: For broad questions (e.g., "show me volatility"), list ALL relevant components (e.g., #47 Price Volatility Analysis, #69 Rolling Volatility, #79 Volatility Heatmap, #80 Volatility Rose Plot, #3 Price Stability Index). Do NOT default to just one.
3. INCLUDE TAB NAVIGATION: Always tell the user which tab to click. Example: "Navigate to the **Volatility & Risk** tab to find **#69 Rolling Volatility**."
4. DATA QUERIES — ONLY USE SNAPSHOT DATA: Provide exact numbers from the DATA SNAPSHOT. If specific data (e.g., a state's price, a specific address) is NOT in the snapshot, say "This data is available in component #X on the [Tab Name] tab." NEVER fabricate prices, product names, percentages, or rankings.
5. DO NOT CONFLATE METRICS: The CERTUS Price Index measures price level relative to baseline (100 = no change). It is NOT a volatility measure. For volatility, direct users to #47, #69, #79, or #3.
6. USE ALL 5 SERVICE FIELDS: When answering pricing questions, reference all relevant fields (Pest365, Pest365+, One-Time, ISV, MTC) from the snapshot — not just Pest365. Note which states have MTC available.
7. FORMATTING: Use **bold** for component numbers and tab names. Keep answers concise (3-8 sentences). Use bullet points for lists of components or data.
8. Be professional, brief, and strategic. No code blocks. No general coding help.`;

export default function HestonChatbot({ analytics, projectId }) {
  const defaultMessages = [{
    role: 'assistant',
    content: `Greetings! I am Heston, your AI Product Manager and Data Analyst. I serve a double-duty here: I know every inch of our 142-component dashboard, and I'm plugged directly into your project's live data.

Ask me things like:
- **Product Analysis:** "Where is the Price War Early Warning (#1)?" or "How does the ARIMA Forecast (#129) work?"
- **Data Deep-Dive:** "What is our portfolio price index today?" or "Which state had the largest price increase?"
- **Portfolio Strategy:** "Compare Washington's volatility against Florida" or "Which states are below our average price?"
- **Future Roadmap:** "Can we add a real-time Weather Impact chart (#156+)?" or "I need a PDF report generator module (#157+)."

How can I assist your strategy today?`
  }];

  const [messages, setMessages] = useState(defaultMessages);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Load chat history from S3 (no 400KB DynamoDB limit)
  useEffect(() => {
    if (!projectId) return;
    getChatHistory(projectId).then(saved => {
      if (Array.isArray(saved) && saved.length > 0) {
        setMessages(saved);
      }
      setHistoryLoaded(true);
    }).catch(e => {
      console.error('Failed to load chat history:', e);
      setHistoryLoaded(true);
    });
  }, [projectId]);

  // Save chat history to S3 (fire-and-forget, only after history loaded)
  const persistChat = useCallback((msgs) => {
    if (projectId && historyLoaded) saveChatHistory(projectId, msgs).catch(() => {});
  }, [projectId, historyLoaded]);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleClear = () => {
    setMessages(defaultMessages);
    persistChat(defaultMessages);
  };

  const prevPriceRef = useRef(0);
  
  useEffect(() => {
    const currentPrice = analytics?.priceIndex?.currentIndex || 0;
    if (prevPriceRef.current !== 0 && Math.abs(currentPrice - prevPriceRef.current) > 0.01) {
      const msg = {
        role: 'assistant',
        content: `📈 **Data Alert detected!** I see the portfolio price index just shifted from ${prevPriceRef.current.toFixed(2)} to ${currentPrice.toFixed(2)}. I have updated my internal mental model with the new simulation results. How should we analyze this shift?`
      };
      setMessages(prev => {
        const updated = [...prev, msg];
        persistChat(updated);
        return updated;
      });
    }
    prevPriceRef.current = currentPrice;
  }, [analytics]);

  const getDataSnapshot = () => {
    if (!analytics) return "No project data available yet.";
    const {
      kpis, stateAnalysis, topMovers, priceIndex, healthScore, priceArchitecture,
      changeEvents, correlations, volatilityMatrix, anomalies, insights,
      priceWarIndicators, stickinessData, serviceAvailability, forecastData,
      priceTiers, ragStatus, distributions, seasonalData, rollingVolatilityData,
      momentumData, regimeData, decileData, psychologicalPricingData, elasticityData,
      herfindahlData, benfordData, valueAtRiskData, grangerCausalityData,
      signalNoiseData, anomalyScorecardData, performanceAttributionData,
      changepointData, markovTransitionData, priceContagionData,
      lyapunovExponentData, bucketMigrationData, priceChangeDistribution,
      monteCarloFanData, lorenzCurveData,
    } = analytics;

    const fmt = (v) => v != null ? `$${Number(v).toFixed(2)}` : 'N/A';
    const pct = (v) => v != null ? `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%` : '';

    let snapshot = "DATA SNAPSHOT FOR THIS PROJECT:\n";

    // --- Portfolio KPIs ---
    if (kpis) {
      snapshot += `- Total Monitored Addresses: ${kpis.totalAddresses || 0}\n`;
      snapshot += `- Total Data Points Collected: ${kpis.totalDataPoints || 0}\n`;
      snapshot += `- Days of History Tracking: ${kpis.trackingSinceDays || 0} days\n`;
      snapshot += `- Portfolio Average Price (Pest365): ${fmt(kpis.overallAvgPrice)}\n`;
      snapshot += `- Portfolio Average Price Change (since baseline): ${pct(kpis.avgPriceChange)}\n`;
      snapshot += `- Addresses with Price Increase: ${kpis.addressesWithIncrease || 0}\n`;
      snapshot += `- Addresses with Price Decrease: ${kpis.addressesWithDecrease || 0}\n`;
      snapshot += `- Addresses Unchanged: ${kpis.addressesUnchanged || 0}\n`;
      if (kpis.daysSinceLastChange != null) {
        snapshot += `- Days Since Last Price Change: ${kpis.daysSinceLastChange}\n`;
      }
      if (kpis.mostExpensiveState) {
        snapshot += `- Most Expensive State: ${kpis.mostExpensiveState.state} (${fmt(kpis.mostExpensiveState.avgPest365)})\n`;
      }
      if (kpis.cheapestState) {
        snapshot += `- Cheapest State: ${kpis.cheapestState.state} (${fmt(kpis.cheapestState.avgPest365)})\n`;
      }
    }

    // --- Fisher Price Index ---
    if (priceIndex && priceIndex.currentIndex) {
      snapshot += `\nAGGREGATE PORTFOLIO PRICE INDEX (FISHER): ${Number(priceIndex.currentIndex).toFixed(2)}`;
      snapshot += ` (100 = baseline${priceIndex.baselineDate ? ', set ' + priceIndex.baselineDate : ''})\n`;
      if (priceIndex.timeline && priceIndex.timeline.length >= 2) {
        const first = priceIndex.timeline[0];
        const last = priceIndex.timeline[priceIndex.timeline.length - 1];
        snapshot += `- Index range: ${Number(first.fisherIndex).toFixed(2)} (${first.date}) → ${Number(last.fisherIndex).toFixed(2)} (${last.date})\n`;
      }
    }

    // --- Health Score with component breakdown ---
    if (healthScore) {
      const overall = healthScore.overallScore ?? healthScore.overall;
      if (overall != null) {
        snapshot += `\nPORTFOLIO HEALTH SCORE: ${overall}/100 (Grade: ${healthScore.grade || 'N/A'})\n`;
        if (healthScore.components && healthScore.components.length > 0) {
          healthScore.components.forEach(c => {
            snapshot += `- ${c.name}: ${c.score}/100 (${c.status}) — ${c.description}\n`;
          });
        }
      }
    }

    // --- State Analysis with ALL 5 service fields ---
    if (stateAnalysis && Array.isArray(stateAnalysis) && stateAnalysis.length > 0) {
      snapshot += "\nSTATE-BY-STATE PRICING (all 5 service fields):\n";
      stateAnalysis.forEach(s => {
        const change = s.changePercent !== undefined ? ` (${pct(s.changePercent)} vs baseline)` : '';
        snapshot += `- ${s.state} [${s.addressCount} addresses]${change}:\n`;
        snapshot += `    Pest365: ${fmt(s.avgPest365)}`;
        snapshot += ` | Pest365+: ${fmt(s.avgPest365Plus)}`;
        snapshot += ` | One-Time: ${fmt(s.avgOneTime)}`;
        snapshot += ` | ISV: ${fmt(s.avgISV)}`;
        if (s.mtcAvailable) {
          snapshot += ` | MTC: ${fmt(s.avgMTC)}`;
        } else {
          snapshot += ` | MTC: not available`;
        }
        snapshot += '\n';
      });

      // Portfolio-wide field averages
      const totalCount = stateAnalysis.reduce((a, s) => a + (s.addressCount || 0), 0);
      if (totalCount > 0) {
        const wavg = (field) => {
          const sum = stateAnalysis.reduce((a, s) => a + ((s[field] || 0) * (s.addressCount || 0)), 0);
          return sum / totalCount;
        };
        snapshot += `\nPORTFOLIO-WIDE AVERAGES (weighted by address count):\n`;
        snapshot += `- Pest365: ${fmt(wavg('avgPest365'))}`;
        snapshot += ` | Pest365+: ${fmt(wavg('avgPest365Plus'))}`;
        snapshot += ` | One-Time: ${fmt(wavg('avgOneTime'))}`;
        snapshot += ` | ISV: ${fmt(wavg('avgISV'))}\n`;
        const mtcStates = stateAnalysis.filter(s => s.mtcAvailable);
        if (mtcStates.length > 0) {
          const mtcCount = mtcStates.reduce((a, s) => a + (s.addressCount || 0), 0);
          const mtcAvg = mtcStates.reduce((a, s) => a + ((s.avgMTC || 0) * (s.addressCount || 0)), 0) / mtcCount;
          snapshot += `- MTC: ${fmt(mtcAvg)} (available in ${mtcStates.length} of ${stateAnalysis.length} states: ${mtcStates.map(s => s.state).join(', ')})\n`;
        } else {
          snapshot += `- MTC: not available in any state\n`;
        }
      }
    }

    // --- Volatility Matrix (overall portfolio volatility per field) ---
    if (volatilityMatrix && volatilityMatrix.matrix && volatilityMatrix.matrix['__overall__']) {
      const overall = volatilityMatrix.matrix['__overall__'];
      snapshot += "\nPORTFOLIO VOLATILITY (coefficient of variation % by service field):\n";
      for (const f of volatilityMatrix.fields || []) {
        const cell = overall[f.key];
        if (cell && cell.cv != null) {
          snapshot += `- ${f.label}: CV=${cell.cv}% (mean=${fmt(cell.mean)}, stdDev=${fmt(cell.stdDev)})\n`;
        }
      }
      // Per-state volatility summary
      if (volatilityMatrix.states && volatilityMatrix.states.length > 0) {
        snapshot += "Per-state Pest365 volatility: ";
        const stateVols = volatilityMatrix.states
          .map(st => ({ state: st, cv: volatilityMatrix.matrix[st]?.pest365?.cv }))
          .filter(s => s.cv != null)
          .sort((a, b) => b.cv - a.cv);
        snapshot += stateVols.map(s => `${s.state}=${s.cv}%`).join(', ') + '\n';
      }
    }

    // --- Price Architecture (spreads between service tiers) ---
    if (priceArchitecture) {
      snapshot += "\nPRICE ARCHITECTURE (tier relationships):\n";
      if (priceArchitecture.pest365PlusSpread != null) snapshot += `- Pest365+ premium over Pest365: ${fmt(priceArchitecture.pest365PlusSpread)} (${pct(priceArchitecture.pest365PlusRatio ? (priceArchitecture.pest365PlusRatio - 1) * 100 : null)})\n`;
      if (priceArchitecture.subscriptionToOneTimeRatio != null) snapshot += `- Subscription-to-OneTime ratio: ${Number(priceArchitecture.subscriptionToOneTimeRatio).toFixed(2)}x\n`;
      if (priceArchitecture.isvToAnnualRatio != null) snapshot += `- ISV-to-Annual ratio: ${Number(priceArchitecture.isvToAnnualRatio).toFixed(2)}x\n`;
    }

    // --- Correlations between service fields ---
    if (correlations && correlations.matrix) {
      const pairs = Object.entries(correlations.matrix).filter(([, v]) => v != null);
      if (pairs.length > 0) {
        snapshot += "\nSERVICE FIELD CORRELATIONS:\n";
        pairs.forEach(([pair, r]) => {
          const [f1, f2] = pair.split('_');
          snapshot += `- ${f1} vs ${f2}: r=${r}\n`;
        });
        if (correlations.keyInsight) {
          snapshot += `Insight: ${correlations.keyInsight}\n`;
        }
      }
    }

    // --- Price Stickiness ---
    if (stickinessData && stickinessData.overall != null) {
      snapshot += `\nPRICE STICKINESS: ${(stickinessData.overall * 100).toFixed(1)}% of addresses unchanged period-over-period (trend: ${stickinessData.trend})\n`;
      if (stickinessData.byField) {
        const labels = { pest365: 'Pest365', pest365Plus: 'Pest365+', oneTime: 'One-Time', initialServiceVisit: 'ISV' };
        const fieldSticky = Object.entries(stickinessData.byField)
          .map(([f, vals]) => ({ field: labels[f] || f, rate: vals.length > 0 ? vals[vals.length - 1] : null }))
          .filter(f => f.rate != null);
        if (fieldSticky.length > 0) {
          snapshot += `By field: ${fieldSticky.map(f => `${f.field}=${(f.rate * 100).toFixed(1)}%`).join(', ')}\n`;
        }
      }
    }

    // --- Top Movers (increases, decreases, most volatile) ---
    if (topMovers) {
      if (topMovers.topIncreases && topMovers.topIncreases.length > 0) {
        snapshot += "\nTOP PRICE INCREASES:\n";
        topMovers.topIncreases.slice(0, 5).forEach(m => {
          snapshot += `- ${m.address}, ${m.city}, ${m.state}: ${fmt(m.previousPrice)} -> ${fmt(m.currentPrice)} (${pct(m.changePercent)}, ${m.field})\n`;
        });
      }
      if (topMovers.topDecreases && topMovers.topDecreases.length > 0) {
        snapshot += "\nTOP PRICE DECREASES:\n";
        topMovers.topDecreases.slice(0, 5).forEach(m => {
          snapshot += `- ${m.address}, ${m.city}, ${m.state}: ${fmt(m.previousPrice)} -> ${fmt(m.currentPrice)} (${pct(m.changePercent)}, ${m.field})\n`;
        });
      }
      if (topMovers.mostVolatile && topMovers.mostVolatile.length > 0) {
        snapshot += "\nMOST VOLATILE ADDRESSES:\n";
        topMovers.mostVolatile.slice(0, 5).forEach(m => {
          snapshot += `- ${m.address}, ${m.city}, ${m.state}: volatility=${Number(m.volatility).toFixed(1)}%, range=${m.priceRange}, changes=${m.changeCount}\n`;
        });
      }
    }

    // --- Fallback for legacy topMovers format ---
    if (topMovers && Array.isArray(topMovers)) {
      snapshot += "\nLATEST TOP PRICE MOVERS:\n";
      topMovers.slice(0, 5).forEach(m => {
        snapshot += `- ${m.label}: $${m.oldPrice} -> $${m.newPrice} (${m.change}%)\n`;
      });
    }

    // --- Price War Indicators ---
    if (priceWarIndicators && priceWarIndicators.overallRisk) {
      snapshot += `\nPRICE WAR RISK: ${priceWarIndicators.overallRisk.toUpperCase()} (score: ${priceWarIndicators.riskScore}/100, trend: ${priceWarIndicators.recentTrend})\n`;
      if (priceWarIndicators.indicators && priceWarIndicators.indicators.length > 0) {
        priceWarIndicators.indicators.forEach(ind => {
          snapshot += `- ${ind.name}: ${ind.value} (${ind.status})\n`;
        });
      }
    }

    // --- Anomaly Summary ---
    if (anomalies && Array.isArray(anomalies) && anomalies.length > 0) {
      const outliers = anomalies.filter(a => a.type === 'outlier');
      const dataQuality = anomalies.filter(a => a.type === 'data_quality');
      const promos = anomalies.filter(a => a.type === 'promo');
      snapshot += `\nANOMALY SUMMARY: ${anomalies.length} total — ${outliers.length} outliers, ${dataQuality.length} data quality issues, ${promos.length} promo detections\n`;
      if (outliers.length > 0) {
        snapshot += "Top outliers:\n";
        outliers.slice(0, 3).forEach(a => {
          snapshot += `- ${a.address} (${a.field}): ${fmt(a.value)}, z-score=${a.zScore}\n`;
        });
      }
    }

    // --- Recent Change Events ---
    if (changeEvents && Array.isArray(changeEvents) && changeEvents.length > 0) {
      snapshot += "\nRECENT CHANGE EVENTS:\n";
      changeEvents.slice(0, 5).forEach(e => {
        snapshot += `- [${e.type || 'change'}] ${e.description || `${e.field}: ${e.details || ''}`}\n`;
      });
    }

    // --- Active Promos ---
    if (kpis && kpis.activePromos && kpis.activePromos.length > 0) {
      snapshot += "\nACTIVE PROMOTIONS DETECTED:\n";
      kpis.activePromos.forEach(p => {
        snapshot += `- ${p.field}: normal ${fmt(p.normalPrice)} -> promo ${fmt(p.promoPrice)} (${p.affectedCount} addresses)\n`;
      });
    }

    // --- MTC Service Availability ---
    if (serviceAvailability && serviceAvailability.mosquitoTickControl) {
      const mtc = serviceAvailability.mosquitoTickControl;
      snapshot += `\nMTC SERVICE AVAILABILITY: ${mtc.availableCount} available, ${mtc.unavailableCount} unavailable\n`;
      if (mtc.byState && mtc.byState.length > 0) {
        snapshot += `By state: ${mtc.byState.map(s => `${s.state} ${s.available}/${s.total}`).join(', ')}\n`;
      }
    }

    // --- Forecast ---
    if (forecastData && forecastData.forecast && forecastData.forecast.length > 0) {
      const last = forecastData.forecast[forecastData.forecast.length - 1];
      snapshot += `\nPRICE FORECAST (90-day): projected Pest365 avg ${fmt(last.predicted)}`;
      if (last.upper != null && last.lower != null) {
        snapshot += ` (range: ${fmt(last.lower)} – ${fmt(last.upper)})`;
      }
      snapshot += '\n';
      if (forecastData.rSquared != null) {
        snapshot += `- Model R-squared: ${Number(forecastData.rSquared).toFixed(3)}\n`;
      }
    }

    // --- Auto-generated Insights ---
    if (insights && Array.isArray(insights) && insights.length > 0) {
      snapshot += "\nAUTO-GENERATED INSIGHTS:\n";
      insights.forEach(ins => {
        snapshot += `- ${ins}\n`;
      });
    }

    // --- Price Tier Distribution ---
    if (priceTiers) {
      snapshot += "\nPRICE TIER DISTRIBUTION:\n";
      for (const [field, tiers] of Object.entries(priceTiers)) {
        if (Array.isArray(tiers) && tiers.length > 0) {
          const labels = { pest365: 'Pest365', pest365Plus: 'Pest365+', oneTime: 'One-Time', initialServiceVisit: 'ISV', mosquitoTickControl: 'MTC' };
          snapshot += `${labels[field] || field}: `;
          const priced = tiers.filter(t => t.value !== 'not_available');
          snapshot += priced.map(t => `$${t.value} (${t.count} addr, ${t.pctOfTotal}%)`).join(', ');
          const na = tiers.find(t => t.value === 'not_available');
          if (na) snapshot += ` | N/A: ${na.count} addr`;
          snapshot += '\n';
        }
      }
    }

    // --- RAG Status Summary ---
    if (ragStatus && Array.isArray(ragStatus) && ragStatus.length > 0) {
      const green = ragStatus.filter(r => r.status === 'green').length;
      const amber = ragStatus.filter(r => r.status === 'amber').length;
      const red = ragStatus.filter(r => r.status === 'red').length;
      snapshot += `\nADDRESS RAG STATUS: ${green} green, ${amber} amber, ${red} red (out of ${ragStatus.length})\n`;
      if (red > 0) {
        snapshot += "Red-flagged addresses:\n";
        ragStatus.filter(r => r.status === 'red').slice(0, 5).forEach(r => {
          snapshot += `- ${r.address}, ${r.city}, ${r.state}: ${r.statusReason}\n`;
        });
      }
    }

    // --- Price Distribution Stats ---
    if (distributions && distributions.current) {
      snapshot += "\nCURRENT PRICE DISTRIBUTIONS (value counts):\n";
      const labels = { pest365: 'Pest365', pest365Plus: 'Pest365+', oneTime: 'One-Time', mosquitoTickControl: 'MTC' };
      for (const [field, values] of Object.entries(distributions.current)) {
        if (field === 'mosquitoTickControlAll') continue;
        if (Array.isArray(values) && values.length > 0) {
          const sorted = [...values].sort((a, b) => a - b);
          const min = sorted[0];
          const max = sorted[sorted.length - 1];
          const median = sorted[Math.floor(sorted.length / 2)];
          const mean = sorted.reduce((a, b) => a + b, 0) / sorted.length;
          snapshot += `- ${labels[field] || field}: n=${values.length}, min=${fmt(min)}, median=${fmt(median)}, mean=${fmt(mean)}, max=${fmt(max)}\n`;
        }
      }
    }

    // --- Seasonal Decomposition ---
    if (seasonalData && seasonalData.seasonal && seasonalData.seasonal.length > 0) {
      const maxSeasonal = Math.max(...seasonalData.seasonal.map(Math.abs));
      snapshot += `\nSEASONALITY (${seasonalData.fieldLabel || 'pest365'}): max seasonal amplitude=$${maxSeasonal.toFixed(2)}\n`;
      if (seasonalData.trend && seasonalData.trend.length >= 2) {
        const validTrend = seasonalData.trend.filter(v => v != null);
        if (validTrend.length >= 2) {
          snapshot += `- Trend direction: ${validTrend[validTrend.length - 1] > validTrend[0] ? 'upward' : validTrend[validTrend.length - 1] < validTrend[0] ? 'downward' : 'flat'}\n`;
        }
      }
    }

    // --- Rolling Volatility ---
    if (rollingVolatilityData && rollingVolatilityData.overall && rollingVolatilityData.overall.length > 0) {
      const vol = rollingVolatilityData.overall;
      const latest = vol[vol.length - 1];
      const avg = vol.reduce((a, b) => a + b, 0) / vol.length;
      snapshot += `\nROLLING VOLATILITY: current=${latest.toFixed(2)}%, avg=${avg.toFixed(2)}%, threshold=${rollingVolatilityData.threshold}%\n`;
      if (rollingVolatilityData.byField) {
        for (const [field, vals] of Object.entries(rollingVolatilityData.byField)) {
          if (Array.isArray(vals) && vals.length > 0) {
            snapshot += `- ${field}: latest=${vals[vals.length - 1].toFixed(2)}%\n`;
          }
        }
      }
    }

    // --- Momentum & RSI ---
    if (momentumData) {
      if (momentumData.rsi && momentumData.rsi.length > 0) {
        const latestRSI = momentumData.rsi[momentumData.rsi.length - 1];
        const status = latestRSI > 70 ? 'OVERBOUGHT' : latestRSI < 30 ? 'OVERSOLD' : 'NEUTRAL';
        snapshot += `\nPRICE MOMENTUM: RSI=${latestRSI.toFixed(1)} (${status}, overbought>${momentumData.overbought}, oversold<${momentumData.oversold})\n`;
      }
      if (momentumData.momentum && momentumData.momentum.length > 0) {
        const latestMom = momentumData.momentum[momentumData.momentum.length - 1];
        snapshot += `- Momentum: ${latestMom > 0 ? '+' : ''}${latestMom.toFixed(2)}\n`;
      }
      if (momentumData.macd) {
        const macdLine = momentumData.macd.line;
        const signalLine = momentumData.macd.signal;
        if (macdLine && signalLine && macdLine.length > 0 && signalLine.length > 0) {
          const signal = macdLine[macdLine.length - 1] > signalLine[signalLine.length - 1] ? 'BULLISH' : 'BEARISH';
          snapshot += `- MACD signal: ${signal}\n`;
        }
      }
    }

    // --- Price Regimes ---
    if (regimeData && regimeData.regimes && regimeData.regimes.length > 0) {
      const currentRegime = regimeData.regimes[regimeData.regimes.length - 1];
      snapshot += `\nPRICE REGIME: currently ${currentRegime.label} (avg=${fmt(currentRegime.avgPrice)}, volatility=${currentRegime.volatility.toFixed(2)}%)\n`;
      snapshot += `Total regimes detected: ${regimeData.regimes.length} (${regimeData.regimes.map(r => r.label).join(' → ')})\n`;
      if (regimeData.changePoints && regimeData.changePoints.length > 0) {
        snapshot += `Change points: ${regimeData.changePoints.map(cp => `${cp.date} (${cp.direction}, magnitude=${cp.magnitude.toFixed(2)})`).join(', ')}\n`;
      }
    }

    // --- Decile Distribution ---
    if (decileData && decileData.currentDistribution && decileData.currentDistribution.length > 0) {
      snapshot += "\nPRICE DECILE DISTRIBUTION (current):\n";
      decileData.currentDistribution.forEach(d => {
        snapshot += `- ${d.decile}: ${d.count} addresses, range ${fmt(d.minPrice)}-${fmt(d.maxPrice)}, avg ${fmt(d.avgPrice)}\n`;
      });
    }

    // --- Psychological Pricing Patterns ---
    if (psychologicalPricingData && Array.isArray(psychologicalPricingData) && psychologicalPricingData.length > 0) {
      snapshot += "\nPSYCHOLOGICAL PRICING PATTERNS (most common price endings):\n";
      psychologicalPricingData.slice(0, 8).forEach(p => {
        snapshot += `- ending '${p.ending}': ${p.count} occurrences\n`;
      });
    }

    // --- Price Elasticity by State ---
    if (elasticityData && Array.isArray(elasticityData) && elasticityData.length > 0) {
      snapshot += "\nPRICE ELASTICITY BY STATE:\n";
      elasticityData.forEach(e => {
        snapshot += `- ${e.state}: elasticity=${e.elasticity.toFixed(2)}, avg=${fmt(e.avgPrice)}, range=$${e.priceRange.toFixed(2)}, change freq=${(e.changeFrequency * 100).toFixed(1)}%\n`;
      });
    }

    // --- Herfindahl-Hirschman Index (market concentration) ---
    if (herfindahlData && herfindahlData.hhi && herfindahlData.hhi.length > 0) {
      const latest = herfindahlData.hhi[herfindahlData.hhi.length - 1];
      const level = latest < (herfindahlData.thresholds?.low || 1500) ? 'LOW (competitive)' :
        latest < (herfindahlData.thresholds?.moderate || 2500) ? 'MODERATE' : 'HIGH (concentrated)';
      snapshot += `\nMARKET CONCENTRATION (HHI): ${latest.toFixed(0)} — ${level}\n`;
      if (herfindahlData.topContributors && herfindahlData.topContributors.length > 0) {
        snapshot += `Top contributors: ${herfindahlData.topContributors.slice(0, 5).map(c => `${c.state} (${(c.share * 100).toFixed(1)}%)`).join(', ')}\n`;
      }
    }

    // --- Benford's Law conformity ---
    if (benfordData && benfordData.fields) {
      snapshot += "\nBENFORD'S LAW ANALYSIS (price digit distribution):\n";
      for (const [field, data] of Object.entries(benfordData.fields)) {
        if (data && data.chiSquare != null) {
          snapshot += `- ${field}: chi²=${data.chiSquare.toFixed(2)}, p=${data.pValue.toFixed(4)}, ${data.conforms ? 'CONFORMS (natural pricing)' : 'DOES NOT CONFORM (possible artificial pricing)'}\n`;
        }
      }
    }

    // --- Value at Risk ---
    if (valueAtRiskData && valueAtRiskData.summary) {
      const s = valueAtRiskData.summary;
      snapshot += `\nVALUE AT RISK (${valueAtRiskData.field || 'pest365'}):\n`;
      snapshot += `- VaR 95%: ${s.currentVar95 != null ? s.currentVar95.toFixed(2) + '%' : 'N/A'} (breach rate: ${s.breachRate95 != null ? (s.breachRate95 * 100).toFixed(1) + '%' : 'N/A'})\n`;
      snapshot += `- VaR 99%: ${s.currentVar99 != null ? s.currentVar99.toFixed(2) + '%' : 'N/A'} (breach rate: ${s.breachRate99 != null ? (s.breachRate99 * 100).toFixed(1) + '%' : 'N/A'})\n`;
      snapshot += `- Max single-period loss: ${s.maxLoss != null ? s.maxLoss.toFixed(2) + '%' : 'N/A'}, avg loss: ${s.avgLoss != null ? s.avgLoss.toFixed(2) + '%' : 'N/A'}\n`;
    }

    // --- Granger Causality ---
    if (grangerCausalityData && grangerCausalityData.significantPairs && grangerCausalityData.significantPairs.length > 0) {
      snapshot += "\nGRANGER CAUSALITY (which fields predict others):\n";
      grangerCausalityData.significantPairs.forEach(p => {
        snapshot += `- ${p.from} → ${p.to}: F=${p.fStat.toFixed(2)}, p=${p.pValue.toFixed(4)} (${p.pValue < 0.05 ? 'SIGNIFICANT' : 'weak'})\n`;
      });
    }

    // --- Signal-to-Noise ---
    if (signalNoiseData && signalNoiseData.overallSNR != null) {
      snapshot += `\nSIGNAL-TO-NOISE RATIO (${signalNoiseData.field || 'pest365'}): SNR=${signalNoiseData.overallSNR.toFixed(2)}, noise std=${signalNoiseData.noiseStd.toFixed(4)}, signal strength=${signalNoiseData.signalStrength}\n`;
    }

    // --- Anomaly Scorecard ---
    if (anomalyScorecardData && anomalyScorecardData.addresses && anomalyScorecardData.addresses.length > 0) {
      const alerts = anomalyScorecardData.addresses.filter(a => a.alertLevel === 'alert');
      const watches = anomalyScorecardData.addresses.filter(a => a.alertLevel === 'watch');
      snapshot += `\nANOMALY SCORECARD: ${alerts.length} alerts, ${watches.length} watches, ${anomalyScorecardData.addresses.length - alerts.length - watches.length} normal\n`;
      if (alerts.length > 0) {
        snapshot += "Alert addresses:\n";
        alerts.slice(0, 5).forEach(a => {
          snapshot += `- ${a.label}, ${a.state}: score=${a.compositeScore.toFixed(1)}/10\n`;
        });
      }
    }

    // --- Performance Attribution ---
    if (performanceAttributionData && performanceAttributionData.attributions) {
      snapshot += `\nPERFORMANCE ATTRIBUTION (total change: ${pct(performanceAttributionData.totalChange)}):\n`;
      performanceAttributionData.attributions.forEach(a => {
        snapshot += `- ${a.factor}: ${pct(a.contribution)} (${a.percent.toFixed(1)}% of total)\n`;
      });
      if (performanceAttributionData.byState && performanceAttributionData.byState.length > 0) {
        snapshot += "By state: ";
        snapshot += performanceAttributionData.byState.slice(0, 5).map(s => `${s.state}=${pct(s.contribution)}`).join(', ') + '\n';
      }
    }

    // --- Changepoints ---
    if (changepointData && changepointData.changepoints && changepointData.changepoints.length > 0) {
      snapshot += `\nSTRUCTURAL CHANGEPOINTS (${changepointData.field || 'pest365'}):\n`;
      changepointData.changepoints.forEach(cp => {
        snapshot += `- ${cp.date}: ${fmt(cp.beforeMean)} → ${fmt(cp.afterMean)} (magnitude=${cp.magnitude.toFixed(2)}, confidence=${(cp.confidence * 100).toFixed(0)}%)\n`;
      });
    }

    // --- Markov Transition Probabilities ---
    if (markovTransitionData && markovTransitionData.matrix && markovTransitionData.states) {
      snapshot += `\nMARKOV TRANSITION PROBABILITIES (${markovTransitionData.field || 'pest365'}):\n`;
      markovTransitionData.states.forEach((state, i) => {
        const probs = markovTransitionData.matrix[i];
        if (probs) {
          snapshot += `- From ${state}: ${markovTransitionData.states.map((s, j) => `→${s}=${(probs[j] * 100).toFixed(1)}%`).join(', ')}\n`;
        }
      });
      if (markovTransitionData.stationaryDistribution) {
        snapshot += `Stationary: ${markovTransitionData.states.map((s, i) => `${s}=${(markovTransitionData.stationaryDistribution[i] * 100).toFixed(1)}%`).join(', ')}\n`;
      }
    }

    // --- Price Contagion ---
    if (priceContagionData && priceContagionData.events && priceContagionData.events.length > 0) {
      snapshot += "\nPRICE CONTAGION (cross-state price spillover):\n";
      priceContagionData.events.slice(0, 8).forEach(e => {
        snapshot += `- ${e.sourceState} → ${e.targetState}: lag=${e.lag} periods, strength=${e.strength.toFixed(2)}, ${e.direction}\n`;
      });
    }

    // --- Lyapunov Exponent (chaos/stability) ---
    if (lyapunovExponentData && lyapunovExponentData.overallExponent != null) {
      snapshot += `\nLYAPUNOV EXPONENT: ${lyapunovExponentData.overallExponent.toFixed(4)} — classification: ${lyapunovExponentData.classification.toUpperCase()}\n`;
      if (lyapunovExponentData.divergenceRate) {
        snapshot += `- Short-term divergence: ${lyapunovExponentData.divergenceRate.shortTerm.toFixed(4)}, long-term: ${lyapunovExponentData.divergenceRate.longTerm.toFixed(4)}\n`;
      }
    }

    // --- Bucket Migration ---
    if (bucketMigrationData && bucketMigrationData.netFlows && bucketMigrationData.netFlows.length > 0) {
      snapshot += `\nPRICE BUCKET MIGRATION (${bucketMigrationData.fromDate} → ${bucketMigrationData.toDate}):\n`;
      bucketMigrationData.netFlows.forEach(f => {
        if (f.netIn !== 0) {
          snapshot += `- ${f.bucket}: net ${f.netIn > 0 ? '+' : ''}${f.netIn} addresses\n`;
        }
      });
    }

    // --- Price Change Distribution ---
    if (priceChangeDistribution && priceChangeDistribution.stats) {
      const s = priceChangeDistribution.stats;
      snapshot += `\nPRICE CHANGE DISTRIBUTION: mean=${pct(s.mean)}, median=${pct(s.median)}, stdDev=${s.stdDev.toFixed(2)}%\n`;
      snapshot += `- Skewness: ${s.skewness.toFixed(2)} (${s.skewness > 0.5 ? 'right-skewed (more large increases)' : s.skewness < -0.5 ? 'left-skewed (more large decreases)' : 'approximately symmetric'})\n`;
      snapshot += `- Changes: ${s.totalChanges} total — ${s.positiveCount} increases, ${s.negativeCount} decreases, ${s.zeroCount} unchanged\n`;
    }

    // --- Monte Carlo Simulation ---
    if (monteCarloFanData && monteCarloFanData.forecast && monteCarloFanData.forecast.median) {
      const fc = monteCarloFanData.forecast;
      const last = fc.median.length - 1;
      if (last >= 0) {
        snapshot += `\nMONTE CARLO SIMULATION (${monteCarloFanData.simulations || 1000} paths, ${monteCarloFanData.field || 'pest365'}):\n`;
        snapshot += `- Median forecast: ${fmt(fc.median[last])}\n`;
        snapshot += `- 10th percentile (pessimistic): ${fmt(fc.p10[last])}\n`;
        snapshot += `- 90th percentile (optimistic): ${fmt(fc.p90[last])}\n`;
        snapshot += `- Interquartile range: ${fmt(fc.p25[last])} – ${fmt(fc.p75[last])}\n`;
      }
    }

    // --- Lorenz Curve / Gini Coefficient ---
    if (lorenzCurveData && lorenzCurveData.fields && lorenzCurveData.fields.length > 0) {
      snapshot += "\nPRICE INEQUALITY (Gini coefficients, 0=perfect equality, 1=total inequality):\n";
      lorenzCurveData.fields.forEach(f => {
        if (f.gini != null) {
          const level = f.gini < 0.2 ? 'very equal' : f.gini < 0.4 ? 'moderate inequality' : 'high inequality';
          snapshot += `- ${f.field}: Gini=${f.gini.toFixed(3)} (${level})\n`;
        }
      });
    }

    return snapshot;
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    const userText = input.trim();
    setInput('');
    setIsTyping(true);

    const currentMessages = [...messages, { role: 'user', content: userText }];
    setMessages(currentMessages);

    try {
      const masterInstruction = `${SYSTEM_PROMPT}\n\n${getDataSnapshot()}\n\nYOU MUST USE THE ABOVE DATA TO ANSWER.`;

      // Sliding window: send only the last 20 messages to avoid exceeding context limits
      // Filter out error messages (429s, 504s) so they don't confuse the AI or pollute context
      const recentMessages = currentMessages.filter(m => m.role !== 'system' && !m.isError);
      const windowedMessages = recentMessages.length > 20
        ? recentMessages.slice(-20)
        : recentMessages;
      // Submit + poll with auto-resubmit on error (Layer 2: frontend retry)
      const MAX_FRONTEND_ATTEMPTS = 2;
      let assistantText;

      for (let frontendAttempt = 0; frontendAttempt < MAX_FRONTEND_ATTEMPTS; frontendAttempt++) {
        try {
          // Step 1: Submit question — Lambda stores it and processes asynchronously
          const submitResponse = await apiService.post('/ai-chat/chat', {
            model: 'glm-5',
            messages: [
              { role: 'system', content: masterInstruction },
              ...windowedMessages.map(m => ({
                role: m.role,
                content: m.content
              }))
            ],
            temperature: 0.7,
            max_tokens: 4096
          }, { timeout: 30000 });

          // Step 2: Poll for result (Lambda processes in background, no 29s API Gateway limit)
          if (submitResponse.requestId && submitResponse.status === 'processing') {
            const maxPollTime = 300000; // 5 minutes max
            const pollInterval = 3000;  // 3 seconds between polls
            const startTime = Date.now();

            while (Date.now() - startTime < maxPollTime) {
              await new Promise(resolve => setTimeout(resolve, pollInterval));
              const pollResponse = await apiService.post('/ai-chat/chat', {
                poll: true,
                requestId: submitResponse.requestId
              }, { timeout: 15000 });

              if (pollResponse.status === 'processing') {
                continue; // still working
              }

              // Got the result
              assistantText = pollResponse.choices?.[0]?.message?.content;
              break;
            }
          } else {
            // Direct response (shouldn't happen with async pattern, but handle gracefully)
            assistantText = submitResponse.choices?.[0]?.message?.content;
          }

          // Detect error-formatted responses (e.g. "Error: Request failed with status code 500")
          // and treat them as failures so auto-resubmit kicks in
          const isErrorResponse = assistantText && assistantText.length < 200
            && /error|failed|status code [45]\d\d/i.test(assistantText);
          if (isErrorResponse) {
            console.log(`Frontend: error response detected: "${assistantText.substring(0, 80)}"`);
            assistantText = null; // clear so retry loop continues
          }

          if (assistantText) break; // success, stop retrying
        } catch (err) {
          // If this was the last attempt, re-throw
          if (frontendAttempt >= MAX_FRONTEND_ATTEMPTS - 1) throw err;
          // Otherwise, wait 5s and auto-resubmit silently
          console.log(`Frontend auto-resubmit (attempt ${frontendAttempt + 2}/${MAX_FRONTEND_ATTEMPTS}) after error: ${err.message}`);
          await new Promise(resolve => setTimeout(resolve, 5000));
        }
      }

      if (!assistantText) {
        assistantText = "I am sorry, I couldn't process that request. Please try again.";
      }

      const finalMessages = [...currentMessages, { role: 'assistant', content: assistantText }];
      setMessages(finalMessages);
      persistChat(finalMessages);
    } catch (error) {
      console.error('Chat error:', error);
      // Mark error messages so they can be filtered from AI context and not persisted
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ Error: ${error.message}`, isError: true }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      <Card style={{ border: '2px solid #0077B5', borderRadius: '12px' }}>
        <CardHeader>
          <CardTitle style={{ color: '#0077B5', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🤖</span> AI Feature Request Chat
          </CardTitle>
        </CardHeader>
        <CardContent style={{ padding: '0' }}>
          <div style={styles.chatContainer}>
            <div style={styles.messagesArea}>
              {messages.map((msg, idx) => (
                <div key={idx} style={{ ...styles.messageRow, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={msg.role === 'user' ? styles.userMessage : styles.assistantMessage}>
                    <FormattedText text={msg.content} />
                  </div>
                </div>
              ))}
              {isTyping && (
                <div style={{ ...styles.messageRow, justifyContent: 'flex-start' }}>
                  <div style={styles.typingIndicator}>
                    <span>🧠</span> Heston is thinking...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div style={styles.inputArea}>
              <input
                style={styles.inputField}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Type your feature request here..."
                disabled={isTyping || !historyLoaded}
              />
              <SecondaryButton onClick={handleClear} style={{ borderRadius: '24px', padding: '10px 20px', fontSize: '13px' }}>
                Clear
              </SecondaryButton>
              <PrimaryButton onClick={handleSend} disabled={!input.trim() || isTyping || !historyLoaded} style={{ borderRadius: '24px', padding: '10px 24px', fontSize: '13px' }}>
                Send
              </PrimaryButton>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
